import { AgentAccessCard } from "@/components/agents/agent-access-card";
import { StallsCard } from "@/components/agents/agent-verifications";
import { CoderSettingsCard } from "@/components/agents/coder-settings-card";
import { RadimVoiceSpendCard } from "@/components/agents/radim-voice-spend-card";
import { RadimWhatsappCard } from "@/components/agents/radim-whatsapp-card";
import { TesterGateCard } from "@/components/agents/tester-gate-card";
import { TesterQueueCard } from "@/components/agents/tester-queue-card";
import { LineChart } from "@/components/charts/line-chart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatTokens, formatUsd } from "@/lib/agents";
import { createFileRoute, useLoaderData, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * `/agents/$slug` overview — what an admin came to check: how it
 * reaches a model, what it has been burning, and (for Prokop) the queue
 * and the runs that ended without a verdict.
 */
export const Route = createFileRoute("/_app/agents/$slug/")({
  component: AgentOverview,
});

function AgentOverview() {
  const { t } = useTranslation();
  const data = useLoaderData({ from: "/_app/agents/$slug" });
  const { slug } = Route.useParams();
  const router = useRouter();

  const tokenSeries = data.daily.map((d) => ({ label: d.day.slice(5), value: d.tokens }));
  const costSeries = data.daily.map((d) => ({ label: d.day.slice(5), value: d.costUsd }));

  return (
    <div className="space-y-4">
      {data.access && (
        <AgentAccessCard
          key={`${data.access.engine}-${data.access.updatedAt}`}
          slug={slug}
          access={data.access}
          onSaved={() => void router.invalidate()}
        />
      )}

      {data.coderSettings && (
        <CoderSettingsCard
          key={data.coderSettings.maxCheckRounds}
          slug={slug}
          settings={data.coderSettings}
          onSaved={() => void router.invalidate()}
        />
      )}

      {data.gate && (
        <TesterGateCard
          key={data.gate.username ?? "none"}
          gate={data.gate}
          onSaved={() => void router.invalidate()}
        />
      )}

      {slug === "reviewer" && <RadimVoiceSpendCard />}

      {slug === "reviewer" && <RadimWhatsappCard />}

      {slug === "tester" && <TesterQueueCard paused={data.agent.status !== "active"} />}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-medium text-sm">
              {t("agents.overview.tokensPerDay")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LineChart data={tokenSeries} showDots={false} formatValue={formatTokens} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-medium text-sm">{t("agents.overview.costPerDay")}</CardTitle>
          </CardHeader>
          <CardContent>
            <LineChart data={costSeries} showDots={false} formatValue={formatUsd} />
          </CardContent>
        </Card>
      </div>

      {data.stalls && <StallsCard stalls={data.stalls} />}
    </div>
  );
}
